import { apiClient } from '@/shared/api/client';
import type {
  PreSetCategory,
  PreSetTask,
  PreSetDocument,
  PreSetFavorite,
  MyPreSet,
} from '../types/preset.types';

const BASE = '/startTask/preset';

// apiClient.get() returns response.data directly (i.e. { success, data })
// So use res.data (not res.data?.data)

// ─── 카테고리 ────────────────────────────────────────────────

export const getCategories = async (countryCd = 'MY'): Promise<PreSetCategory[]> => {
  const res = await apiClient.get(`${BASE}/categories`, { params: { countryCd } });
  return res.data || [];
};

// ─── 태스크 ──────────────────────────────────────────────────

export const getTasksByCategory = async (categoryId: number): Promise<PreSetTask[]> => {
  const res = await apiClient.get(`${BASE}/tasks`, { params: { categoryId } });
  return res.data || [];
};

export const getAllTasks = async (countryCd = 'MY'): Promise<PreSetTask[]> => {
  const res = await apiClient.get(`${BASE}/tasks/all`, { params: { countryCd } });
  return res.data || [];
};

export const getTaskDocuments = async (presetTaskId: number): Promise<PreSetDocument[]> => {
  const res = await apiClient.get(`${BASE}/tasks/${presetTaskId}/documents`);
  const docs: PreSetDocument[] = res.data || [];
  // Defensive: some template names already include ".docx" and UI/other layers may append it again.
  // Normalize repeated extensions (".docx.docx" -> ".docx") at the source.
  return (docs || []).map((d: any) => {
    const rawName = String(d?.templateName ?? '').trim();
    const normalizedName = rawName.replace(/(\.docx)+$/i, '.docx');
    return { ...d, templateName: normalizedName };
  });
};

/** 클라이언트별 문서 순서 저장 (드래그앤드롭). order = docCode 순서 배열 */
export const saveTaskDocOrder = async (presetTaskId: number, order: string[]): Promise<void> => {
  await apiClient.put(`${BASE}/tasks/${presetTaskId}/doc-order`, { order });
};

// ─── 즐겨찾기 ────────────────────────────────────────────────

export const getFavorites = async (): Promise<PreSetFavorite[]> => {
  const res = await apiClient.get(`${BASE}/favorites`);
  return res.data || [];
};

export const addFavorite = async (presetTaskId: number): Promise<void> => {
  await apiClient.post(`${BASE}/favorites`, { presetTaskId });
};

export const removeFavorite = async (presetTaskId: number): Promise<void> => {
  await apiClient.delete(`${BASE}/favorites/${presetTaskId}`);
};

// ─── MyPreSet ────────────────────────────────────────────────

export const getMyPreSets = async (): Promise<MyPreSet[]> => {
  const res = await apiClient.get(`${BASE}/my-presets`);
  return res.data || [];
};

export const createMyPreSet = async (presetName: string, presetData: any): Promise<number | null> => {
  const res = await apiClient.post(`${BASE}/my-presets`, { presetName, presetData });
  return res.data?.myPresetId || null;
};

export const updateMyPreSet = async (id: number, presetName: string, presetData: any): Promise<void> => {
  await apiClient.put(`${BASE}/my-presets/${id}`, { presetName, presetData });
};

export const deleteMyPreSet = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE}/my-presets/${id}`);
};

// ─── 사용 로그 ───────────────────────────────────────────────

export const logPreSetUse = async (presetTaskId: number, taskId: number): Promise<void> => {
  await apiClient.post(`${BASE}/use-log`, { presetTaskId, taskId });
};

// ─── My PreSet (클라이언트별 커스텀 프리셋, 세션 CLIENT_CD 스코프) ──
const MY_BASE = '/startTask/mypreset';

export const getMyCategories = async (countryCd = 'MY'): Promise<PreSetCategory[]> => {
  const res = await apiClient.get(`${MY_BASE}/categories`, { params: { countryCd } });
  return res.data || [];
};

export const getMyAllTasks = async (countryCd = 'MY'): Promise<PreSetTask[]> => {
  const res = await apiClient.get(`${MY_BASE}/tasks/all`, { params: { countryCd } });
  return res.data || [];
};

export const getMyTaskDocuments = async (presetTaskId: number): Promise<PreSetDocument[]> => {
  const res = await apiClient.get(`${MY_BASE}/tasks/${presetTaskId}/documents`);
  const docs: PreSetDocument[] = res.data || [];
  return (docs || []).map((d: any) => {
    const rawName = String(d?.templateName ?? '').trim();
    const normalizedName = rawName.replace(/(\.docx)+$/i, '.docx');
    return { ...d, templateName: normalizedName };
  });
};

/** My Preset 문서 순서 저장 (드래그앤드롭). order = docCode 순서 배열 */
export const saveMyTaskDocOrder = async (presetTaskId: number, order: string[]): Promise<void> => {
  await apiClient.put(`${MY_BASE}/tasks/${presetTaskId}/doc-order`, { order });
};
